/* ---------------------------------------------------------------------------------------------
   AAO NATIVE REVIEWS - metaobject mode behaviour
   ---------------------------------------------------------------------------------------------
   Companion to snippets/aao-reviews-list.liquid, which server-renders every review card, the
   histogram, the averages and the JSON-LD from Shopify metaobjects.

   So this file fetches NOTHING and renders no review content. It only supplies the behaviour the
   markup cannot express on its own:

     * "Load more reviews"  - clears `hidden` on the next page of cards that are already in the DOM
     * histogram filter     - clicking a star row narrows to that rating, re-paging as it goes
     * photo lightbox       - reads the card's data-photos (full-size URLs baked by Liquid)
     * write-a-review panel - opens Shopify's native contact form, keeps the subject in step
     * rating badges        - only for a group whose derived rating_avg/rating_count are missing;
                              snippets/aao-reviews-boot.liquid then publishes the computed figures
                              as window.AAO_REVIEW_STATS and the badges are painted from that.
                              With the caches present this does nothing at all.

   The legacy engine (assets/aao-reviews.js) is not loaded in this mode. Both are kept while the
   switch in snippets/aao-reviews-flags.liquid can still be flipped back.
   --------------------------------------------------------------------------------------------- */

(function () {
  'use strict';

  if (window.AAOReviewsMO) return;

  var PAGE_SIZE = 5;

  var STAR_FILL =
    'M24 9.425c0 .212-.125.443-.375.693l-5.236 5.105 1.24 7.212c.01.067.015.164.015.289a.85.85 0 0 1-.151.511.51.51 0 0 1-.44.21c-.183 0-.375-.058-.577-.174L12 19.869l-6.476 3.404c-.212.115-.404.173-.577.173-.202 0-.353-.07-.454-.21a.85.85 0 0 1-.152-.511c0-.058.01-.154.03-.289l1.24-7.211-5.25-5.106C.12 9.858 0 9.628 0 9.425c0-.355.27-.577.808-.663l7.24-1.053 3.245-6.562c.183-.395.418-.592.707-.592s.524.197.707.592l3.245 6.562 7.24 1.053c.539.086.808.308.808.663Z';
  var STAR_LINE =
    'm16.399 14.574 4.413-4.283-6.086-.894L12 3.887l-2.726 5.51-6.086.894L7.6 14.574l-1.053 6.072L12 17.776l5.438 2.87-1.039-6.072ZM24 9.425c0 .212-.125.443-.375.693l-5.236 5.105 1.24 7.212c.01.067.015.164.015.289 0 .48-.197.72-.591.72-.183 0-.375-.057-.577-.172L12 19.867l-6.476 3.404c-.212.115-.404.173-.577.173-.202 0-.353-.07-.454-.21a.85.85 0 0 1-.152-.511c0-.058.01-.154.03-.289l1.24-7.211-5.25-5.106C.12 9.858 0 9.628 0 9.425c0-.355.27-.577.808-.663l7.24-1.053 3.245-6.562c.183-.395.418-.592.707-.592s.524.197.707.592l3.245 6.562 7.24 1.053c.539.086.808.308.808.663Z';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Same snapping as snippets/aao-review-stars.liquid and the legacy engine: empty / half / full. */
  function starPct(rating, index) {
    var d = rating - (index - 1);
    if (d >= 0.75) return 100;
    if (d >= 0.25) return 50;
    return 0;
  }

  function starsHTML(rating, label) {
    var out =
      '<span class="aao-stars" role="img" aria-label="' +
      esc(label || Math.round(rating * 10) / 10 + ' out of 5 stars') +
      '">';
    for (var i = 1; i <= 5; i++) {
      out +=
        '<svg class="aao-star" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style="--aao-star-pct:' +
        starPct(rating, i) +
        '%"><path class="aao-star__fill" fill="currentColor" d="' +
        STAR_FILL +
        '"/><path class="aao-star__line" fill="currentColor" d="' +
        STAR_LINE +
        '"/></svg>';
    }
    return out + '</span>';
  }

  /* --- lightbox ------------------------------------------------------------------------------ */

  var lightbox = null;

  function onKey(e) {
    if (e.key === 'Escape') closeLightbox();
  }

  function closeLightbox() {
    if (!lightbox) return;
    document.removeEventListener('keydown', onKey);
    if (lightbox.parentNode) lightbox.parentNode.removeChild(lightbox);
    lightbox = null;
  }

  function openLightbox(photos, index, caption) {
    closeLightbox();
    var i = index || 0;
    lightbox = document.createElement('div');
    lightbox.className = 'aao-reviews-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.innerHTML =
      '<button type="button" class="aao-reviews-lightbox__close" aria-label="Close">&times;</button>' +
      (photos.length > 1
        ? '<button type="button" class="aao-reviews-lightbox__nav" data-dir="prev" aria-label="Previous photo">&#8249;</button>' +
          '<button type="button" class="aao-reviews-lightbox__nav" data-dir="next" aria-label="Next photo">&#8250;</button>'
        : '') +
      '<img alt="' + esc(caption || 'Customer photo') + '">' +
      '<div class="aao-reviews-lightbox__caption"></div>';

    var img = lightbox.querySelector('img');
    var cap = lightbox.querySelector('.aao-reviews-lightbox__caption');

    function show() {
      img.src = photos[i];
      cap.textContent = photos.length > 1 ? i + 1 + ' / ' + photos.length : '';
    }

    lightbox.addEventListener('click', function (e) {
      var dir = e.target.getAttribute && e.target.getAttribute('data-dir');
      if (dir === 'prev') {
        i = (i - 1 + photos.length) % photos.length;
        show();
      } else if (dir === 'next') {
        i = (i + 1) % photos.length;
        show();
      } else if (e.target === lightbox || e.target.classList.contains('aao-reviews-lightbox__close')) {
        closeLightbox();
      }
    });

    document.addEventListener('keydown', onKey);
    document.body.appendChild(lightbox);
    show();
  }

  function cardPhotos(card) {
    var raw = card.getAttribute('data-photos');
    if (!raw) return [];
    try {
      var list = JSON.parse(raw);
      return Array.isArray(list) ? list.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  /* --- write-a-review form -------------------------------------------------------------------
     Identical to the legacy engine's wireForm(): the form itself is Shopify's native
     {% form 'contact' %}, server-rendered by snippets/aao-reviews-widget.liquid. */

  function wireForm(root) {
    var host = document.getElementById('aao-review-form-host');
    if (!host) return;

    var outcome = host.querySelector('[data-aao-posted], [data-aao-errors]');
    if (outcome && host.getAttribute('data-open') !== 'true') {
      host.setAttribute('data-open', 'true');
      if (!host.__aaoFocused) {
        host.__aaoFocused = true;
        try {
          outcome.focus({ preventScroll: true });
          outcome.scrollIntoView({ block: 'center' });
        } catch (e) {
          /* focus/scroll are a nicety, never a failure */
        }
      }
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-aao-write]'), function (btn) {
      if (btn.__aaoWired) return;
      btn.__aaoWired = true;
      btn.setAttribute('aria-expanded', host.getAttribute('data-open') === 'true' ? 'true' : 'false');
      btn.setAttribute('aria-controls', 'aao-review-form-host');
      btn.addEventListener('click', function () {
        var open = host.getAttribute('data-open') === 'true';
        host.setAttribute('data-open', open ? 'false' : 'true');
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (!open) {
          var first = host.querySelector('input[name="contact[name]"]');
          if (first) first.focus();
        }
      });
    });

    if (host.__aaoWired) return;
    host.__aaoWired = true;

    var subject = host.querySelector('[data-aao-subject]');
    if (subject) {
      host.addEventListener('change', function (e) {
        if (!e.target || e.target.name !== 'contact[Review rating]') return;
        subject.value = String(subject.getAttribute('data-template') || '').replace(
          '{rating}',
          e.target.value
        );
      });
    }
  }

  /* --- widget -------------------------------------------------------------------------------- */

  function wireWidget(root) {
    var grid = root.querySelector('.aao-reviews__grid');
    var moreWrap = root.querySelector('.aao-reviews__more');
    var empty = root.querySelector('[data-aao-empty]');
    var cards = grid ? Array.prototype.slice.call(grid.querySelectorAll('.aao-review')) : [];
    var filter = 0;

    /* The server already revealed the first page, so start from what is on screen rather than
       redrawing - no flash, and no work at all until the visitor interacts. */
    var shown = cards.filter(function (c) {
      return !c.hidden;
    }).length;

    function visible() {
      return filter
        ? cards.filter(function (c) {
            return Number(c.getAttribute('data-rating')) === filter;
          })
        : cards;
    }

    function draw(reset) {
      if (reset) {
        cards.forEach(function (c) {
          c.hidden = true;
        });
        shown = 0;
      }
      var list = visible();
      list.slice(shown, shown + PAGE_SIZE).forEach(function (c) {
        c.hidden = false;
      });
      shown = Math.min(shown + PAGE_SIZE, list.length);
      if (moreWrap) moreWrap.hidden = shown >= list.length;
      if (empty) empty.hidden = list.length > 0;
    }

    if (moreWrap) {
      var moreBtn = moreWrap.querySelector('button');
      if (moreBtn) {
        moreBtn.addEventListener('click', function () {
          draw(false);
        });
      }
    }

    if (grid) {
      grid.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('.aao-review__photo') : null;
        if (!btn || !grid.contains(btn)) return;
        var card = btn.closest('.aao-review');
        if (!card) return;
        var photos = cardPhotos(card);
        if (!photos.length) return;
        var name = card.querySelector('.aao-review__title span');
        openLightbox(photos, Number(btn.getAttribute('data-photo-index')) || 0, name && name.textContent);
      });
    }

    var header = root.querySelector('.aao-reviews__header');
    var distBtn = root.querySelector('[data-aao-dist]');
    if (header && distBtn) {
      distBtn.addEventListener('click', function () {
        var open = header.getAttribute('data-dist-open') === 'true';
        header.setAttribute('data-dist-open', open ? 'false' : 'true');
        distBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    }

    var rows = root.querySelectorAll('.aao-reviews__dist-row');
    Array.prototype.forEach.call(rows, function (row) {
      function toggle() {
        var v = Number(row.getAttribute('data-rating'));
        filter = filter === v ? 0 : v;
        Array.prototype.forEach.call(rows, function (r) {
          r.setAttribute(
            'aria-pressed',
            Number(r.getAttribute('data-rating')) === filter ? 'true' : 'false'
          );
        });
        draw(true);
      }
      row.addEventListener('click', toggle);
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
    });

    wireForm(root);
  }

  /* --- badges without a derived cache --------------------------------------------------------- */

  function paintBadges(scope) {
    var stats = window.AAO_REVIEW_STATS;
    var badges = scope.querySelectorAll('.aao-rating[data-aao-group]:not([data-aao-ready])');
    Array.prototype.forEach.call(badges, function (el) {
      el.setAttribute('data-aao-ready', '');
      var row = stats ? stats[el.getAttribute('data-aao-group')] : null;
      if (!row || !row.count) return;
      el.innerHTML =
        starsHTML(row.avg, row.avg + ' out of 5 stars') +
        (el.getAttribute('data-hide-count') === 'true'
          ? ''
          : '<span class="aao-rating__count">(' + row.count + ')</span>');
      el.setAttribute('data-rating', row.avg);
      el.setAttribute('data-count', row.count);
    });
  }

  /* --- boot ---------------------------------------------------------------------------------- */

  function hydrate(scope) {
    var host = scope || document;
    paintBadges(host);
    Array.prototype.forEach.call(
      host.querySelectorAll('.aao-reviews[data-aao-mode="metaobjects"]:not([data-aao-ready])'),
      function (root) {
        root.setAttribute('data-aao-ready', '');
        try {
          wireWidget(root);
        } catch (e) {
          if (window.console) console.error('[aao-reviews-mo]', e);
        }
      }
    );
  }

  window.AAOReviewsMO = { hydrate: hydrate, stars: starsHTML };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      hydrate();
    });
  } else {
    hydrate();
  }

  /* Prestige swaps sections in via the Section Rendering API (quick view, variant changes,
     infinite-scroll collections, theme editor). Re-wire whatever arrives. */
  document.addEventListener('shopify:section:load', function () {
    hydrate();
  });
  if (window.MutationObserver) {
    var queued = false;
    new MutationObserver(function (records) {
      if (queued) return;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          queued = true;
          requestAnimationFrame(function () {
            queued = false;
            hydrate();
          });
          return;
        }
      }
    }).observe(document.body || document.documentElement, { childList: true, subtree: true });
  }
})();
